import { MutationCache, QueryCache, QueryClient, type Query } from "@tanstack/react-query";
import { getStatusError } from "@/utils/utils";
import { enqueueUiError } from "@/utils/errors/uiErrorMessage";
import { obterErroApi } from "@/utils/errors/apiError";

const NON_RETRYABLE_QUERY_STATUSES = new Set<number>([401, 403, 404, 410]);
const MSG_OFFLINE = "Sem conexão com a internet. Verifique sua rede";
const MSG_FALHA_COMUNICACAO = "Não foi possível comunicar com o serviço. Tente novamente mais tarde";

export function mustPersistQuery(query: Query): boolean {
	return query.meta?.persist === true;
}

function isAbortOrCancelError(error: unknown): boolean {
	if (error instanceof DOMException) return error.name === "AbortError";
	if (!(error instanceof Error)) return false;
	return /abort|cancel/i.test(`${error.name} ${error.message}`);
}

function isBrowserNetworkFailure(error: unknown): boolean {
	if (!(error instanceof Error) || isAbortOrCancelError(error)) return false;
	return /failed to fetch|networkerror|load failed|preflight/i.test(error.message);
}

function notifyQueryError(error: unknown, meta?: Record<string, unknown>): void {
	if (isAbortOrCancelError(error)) return;
	if (meta?.silenciarErro === true) return;

	const corpoErroApi = obterErroApi(error);
	if (corpoErroApi) {
		enqueueUiError({
			message: corpoErroApi.message,
			variant: corpoErroApi.severity ?? "error",
			options: { preventDuplicate: true, key: `api-error-${corpoErroApi.code ?? corpoErroApi.message}` },
		});
		return;
	}

	// Sem corpo padrão: só notifica infra (HTTP 404/5xx sem corpo) ou falha de rede/offline.
	const status = getStatusError(error);
	const infraHttp = status === 404 || (status !== undefined && status >= 500);
	if (!infraHttp && !isBrowserNetworkFailure(error)) return;

	const offline = typeof navigator !== "undefined" && !navigator.onLine;
	enqueueUiError({
		message: offline ? MSG_OFFLINE : MSG_FALHA_COMUNICACAO,
		options: {
			preventDuplicate: true,
			key: offline ? "global-user-offline" : "global-server-communication-failure",
		},
	});
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
	if (isAbortOrCancelError(error)) return false;

	const status = getStatusError(error);
	if (status !== undefined && NON_RETRYABLE_QUERY_STATUSES.has(status)) return false;

	return failureCount < 1;
}

export const queryClient = new QueryClient({
	queryCache: new QueryCache({ onError: (error, query) => notifyQueryError(error, query.meta) }),
	mutationCache: new MutationCache({
		onError: (error, _variables, _context, mutation) => notifyQueryError(error, mutation.options.meta),
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: shouldRetryQuery,
		},
		dehydrate: {
			shouldDehydrateQuery: mustPersistQuery,
		},
	},
});
